import React, { useMemo } from 'react';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useAppSelector } from 'reducers/useAppSelector';
import type { WProjectListingView, WCardLiteView } from '@zola/svc-web-api-ts-client';
import { CheckIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Check';
import { FavoriteSuiteTogglePlaceholder } from 'favorites/components/FavoriteSuiteToggle/FavoriteSuiteTogglePlaceholder';
import { getUserContext } from 'selectors/user/userSelectors';
import { compactCurrencyFilter } from 'components/product/PricingUtils';
import { CardImage } from 'components/common/ui/CardImage';
import type { CardImageSize } from 'components/common/ui/CardImage/types';
import { CardTileActions } from './CardTileActions';
import { mapCardTypes } from './utils';

import styles from './CardTile.module.less';

const FavoriteSuiteToggle = dynamic(() => import('favorites/components/FavoriteSuiteToggle'), {
  ssr: false,
  loading: () => <FavoriteSuiteTogglePlaceholder height={20} className={styles.favoriteToggle} />,
});

const CARD_IMAGE_SIZES: CardImageSize[] = [{ width: 145, media: '(max-width: 767px)' }];

interface InDraftProps {
  className?: string;
}

export const InDraft: React.FC<InDraftProps> = ({ className }) => (
  <div className={cx(styles.cardInDraftTag, className)}>
    <CheckIcon title="" height={20} width={20} />
    Already in your drafts!
  </div>
);

interface CardTileProps {
  activeColor: string;
  card: WCardLiteView;
  updateDrafts?: () => Promise<void>;
  drafts?: WProjectListingView[];
  position: number;
}

export const CardTile: React.FC<CardTileProps> = ({ activeColor, card, drafts = [], position }) => {
  const userContext = useAppSelector(getUserContext);
  const showIsInDraft = useMemo(() => !userContext.is_guest && drafts.length > 0, [
    drafts.length,
    userContext.is_guest,
  ]);

  const [defaultColor] = Object.keys(card.imageUrlsByColor || {}) || [];

  const [name] = card.name?.split(' - ') || [];
  const cardInfo = card.imageUrlsByColor?.[activeColor] || card.imageUrlsByColor?.[defaultColor];
  const thumbnail = cardInfo?.plpFrontImageUrl;
  const priceCents = cardInfo?.minPriceCents || 0;

  return (
    <div className={styles.card}>
      <div className={styles.cardImage}>
        <CardImage
          src={thumbnail}
          alt={name}
          className={styles.cardImage}
          sourceSizes={CARD_IMAGE_SIZES}
          defaultWidth={200}
        />
        <CardTileActions
          card={card}
          color={activeColor || defaultColor}
          priceCents={priceCents}
          thumbnail={thumbnail}
          position={position}
        />
        {showIsInDraft ? <InDraft /> : null}
        <FavoriteSuiteToggle
          className={styles.favoriteToggle}
          height={20}
          suiteType={mapCardTypes(card.cardType)}
          suiteUUID={card.uuid || ''}
        />
      </div>
      <div className={styles.cardInfo}>
        <div className={styles.cardName}>{name}</div>
        {priceCents > 0 && (
          <div className={styles.cardPrices}>
            From {compactCurrencyFilter(priceCents / 100)} Ea.
          </div>
        )}
      </div>
    </div>
  );
};
